import Sentry, { featureTags } from '@/services/sentry'
import { buildSettingRegistry } from '@/business-logic/user-preferences/userPreferenceDiscovery'
import { USER_PREFERENCE_TYPES, findUserPreferenceDefinition } from '@/business-logic/user-preferences/userPreferenceValues'

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== ''

const FIELDS_OWNED_BY_THE_USER_PREFERENCE = ['label', 'default', 'type', 'group']

const findTeamSettingProblem = (setting, userPreferenceRegistry) => {
  if (!setting || typeof setting !== 'object') return 'the module does not export a setting object'
  if (!isNonEmptyString(setting.key)) return 'key must be a non-empty string'
  const preference = findUserPreferenceDefinition(userPreferenceRegistry, setting.key)
  if (!preference) return `key "${setting.key}" is not a registered user preference`
  if (preference.type !== USER_PREFERENCE_TYPES.BOOLEAN) return 'team policies only apply to an on/off (boolean) user preference'
  if (!isNonEmptyString(setting.description)) return 'description must be a non-empty string'
  const ownedField = FIELDS_OWNED_BY_THE_USER_PREFERENCE.find((field) => setting[field] !== undefined)
  return ownedField ? `${ownedField} comes from the user preference, leave it out` : null
}

const reportRejectedTeamSetting = (moduleName, problem) => {
  const message = `Skipped team advanced setting module "${moduleName}": ${problem}`
  // eslint-disable-next-line no-console
  console.warn(message)
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: featureTags({ featureName: 'team-advanced-settings', taskId: 'CORE-46' }),
  })
}

/**
 * Builds the team advanced setting registry from a webpack `require.context` over the `settings` folder. Each
 * module names a user preference by `key` and carries only team copy; the label and group are read from that
 * preference.
 * A module that fails validation or reuses an earlier key is skipped with a warning, never a crash.
 */
export const buildTeamAdvancedSettingRegistry = (settingsContext, userPreferenceRegistry) => {
  const settings = buildSettingRegistry(settingsContext, {
    findProblem: (setting) => findTeamSettingProblem(setting, userPreferenceRegistry),
    reportRejected: reportRejectedTeamSetting,
  })
  return Object.freeze(settings.map((setting) => {
    const { label, group } = findUserPreferenceDefinition(userPreferenceRegistry, setting.key)
    return Object.freeze({ ...setting, label, ...(group && { group }) })
  }))
}
